import React, { useState, useEffect, useRef, useCallback } from "react"
import { TAgetBrandApplications } from "../../../services/applicationAPI"
import { TAgetBrandFromIds } from "../../../services/brandAPI"
import { useNavigate } from "react-router-dom"
import { useDispatch } from "react-redux"
import { setPageTitle } from "../../../redux/store/themeConfigSlice"
import BrandDetailsModal from "../applicationStats/components/BrandDetailsModal"
import { applicationStateLabels } from "../../Campaign/findCampaign/FindCampaign"
import { isAbortError } from "../../../types/error"

interface BrandData {
  brandId: string
  count: number
}

interface StateData {
  state: string
  total: number
  brands: BrandData[]
}

const LiveApplicationStats: React.FC = () => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const abortControllerRef = useRef<AbortController | null>(null)
  const [data, setData] = useState<StateData[]>([])
  const [loading, setLoading] = useState<boolean>(true)
  const [error, setError] = useState<string | null>(null)
  const [showBrandModal, setShowBrandModal] = useState(false)
  const [selectedModalData, setSelectedModalData] = useState<{
    from: string
    to: string
    brands: { brand_id: string; count: number; brandName?: string }[]
    date: string
  } | null>(null)
  const [loadingBrands, setLoadingBrands] = useState<boolean>(false)

  const fetchData = useCallback(async () => {
    setError(null)
    setLoading(true)

    if (abortControllerRef.current) {
      abortControllerRef.current.abort()
    }

    abortControllerRef.current = new AbortController()

    try {
      const result = await TAgetBrandApplications(abortControllerRef.current.signal)
      setData(result)
    } catch (err: unknown) {
      const error = err as {
        name?: string
        code?: string
        response?: { status: number }
        message?: string
      }

      if (isAbortError(error)) {
        setLoading(false)
        return
      }

      if (error.response && error.response.status === 401) {
        navigate("/login")
        setLoading(false)
        return
      }
      setError(error.message || "Veriler alınırken bir hata oluştu.")
    } finally {
      setLoading(false)
    }
  }, [navigate])

  useEffect(() => {
    dispatch(setPageTitle("Canlı Başvuru İstatistikleri"))
    fetchData()
  }, [dispatch, fetchData])

  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort()
      }
    }
  }, [])

  const handleStateClick = async (stateData: StateData) => {
    if (stateData.brands.length === 0) {
      return
    }

    setLoadingBrands(true)

    try {
      const brandIds = stateData.brands.map((brand) => brand.brandId)
      const brandDataArray = await TAgetBrandFromIds(brandIds)

      const brandsWithNames = stateData.brands.map((brand) => {
        const matchingBrand = brandDataArray.find(
          (data: { _id: string; brand_name: string }) => data._id === brand.brandId,
        )
        return {
          brand_id: brand.brandId,
          count: brand.count,
          brandName: matchingBrand ? matchingBrand.brand_name : brand.brandId,
        }
      })

      setSelectedModalData({
        from: "Toplam Başvuru",
        to: stateData.state,
        brands: brandsWithNames.sort((a, b) => b.count - a.count),
        date: new Date().toISOString().split("T")[0],
      })
      setShowBrandModal(true)
    } catch (error) {
      const brandsWithIds = stateData.brands.map((brand) => ({
        brand_id: brand.brandId,
        count: brand.count,
        brandName: brand.brandId,
      }))
      setSelectedModalData({
        from: "Toplam Başvuru",
        to: stateData.state,
        brands: brandsWithIds.sort((a, b) => b.count - a.count),
        date: new Date().toISOString().split("T")[0],
      })
      setShowBrandModal(true)
    } finally {
      setLoadingBrands(false)
    }
  }

  const totalApplications = data.reduce((sum, state) => sum + state.total, 0)

  const userSideStates = [
    "invite_new",
    "waiting_address",
    "address_rejected",
    "waiting_content",
    "content_rejected",
    "ugc_rejected",
    "content_approved",
  ]

  const companySideStates = [
    "invite_success",
    "first_application",
    "address_to_approve",
    "content_to_share",
    "content_shared",
    "ugc_content",
  ]

  const userSideData = data.filter((item) => userSideStates.includes(item.state))
  const companySideData = data.filter((item) => companySideStates.includes(item.state))

  const userSideTotal = userSideData.reduce((sum, state) => sum + state.total, 0)
  const companySideTotal = companySideData.reduce((sum, state) => sum + state.total, 0)

  const closeBrandModal = () => {
    setShowBrandModal(false)
    setSelectedModalData(null)
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="text-red-600 mb-4 text-xl">⚠️ {error}</div>
          <button
            className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-3 px-6 rounded-lg transition-colors"
            onClick={fetchData}
          >
            Tekrar Dene
          </button>
        </div>
      </div>
    )
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-blue-50 to-purple-50">
        <div className="flex flex-col items-center space-y-6">
          <div className="relative">
            <div className="animate-spin rounded-full h-20 w-20 border-t-4 border-b-4 border-blue-600"></div>
            <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
              <div className="animate-pulse">
                <span className="text-3xl">📊</span>
              </div>
            </div>
          </div>
          <div className="text-center">
            <h3 className="text-xl font-semibold text-gray-800 mb-2">
              Canlı İstatistikler Yükleniyor
            </h3>
            <p className="text-gray-600">Veriler hazırlanıyor, lütfen bekleyin...</p>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="container mx-auto p-6 space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-lg p-6 text-white">
        <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4">
          <div>
            <h1 className="text-3xl font-bold">📊 Canlı Başvuru İstatistikleri</h1>
            <p className="text-blue-100 mt-2">
              Son 3 ay içindeki aktif başvuruların anlık durumu ve marka bazlı dağılımı
            </p>
          </div>
          <div className="flex items-center space-x-3">
            <div className="bg-white/10 rounded-lg px-4 py-2">
              <div className="text-sm text-blue-100">Toplam Başvuru</div>
              <div className="text-2xl font-bold">{totalApplications.toLocaleString("tr-TR")}</div>
            </div>
            <button
              className="bg-white/10 hover:bg-white/20 rounded-lg p-3 transition-colors"
              onClick={fetchData}
              disabled={loading}
              title="Yenile"
            >
              <span className={`text-xl ${loading ? "animate-spin" : ""}`}>🔄</span>
            </button>
          </div>
        </div>
      </div>

      {/* Stats Tables - Kullanıcı Tarafı */}
      <div className="bg-white rounded-lg shadow-lg overflow-hidden border border-gray-200">
        <div className="bg-gradient-to-r from-gray-300 to-gray-300 px-6 py-4">
          <h2 className="text-xl font-bold text-white flex items-center">
            <span className="mr-2">👤</span>
            Kullanıcı Tarafı
            <span className="ml-4 bg-white/20 px-3 py-1 rounded-full text-sm">
              {userSideTotal.toLocaleString("tr-TR")} Başvuru
            </span>
          </h2>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-100">
              <tr>
                <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">Durum</th>
                <th className="px-6 py-4 text-center text-sm font-semibold text-gray-700">
                  Toplam Başvuru
                </th>
                <th className="px-6 py-4 text-center text-sm font-semibold text-gray-700">
                  Marka Sayısı
                </th>
                <th className="px-6 py-4 text-center text-sm font-semibold text-gray-700">Detay</th>
              </tr>
            </thead>
            <tbody>
              {userSideData.map((stateData, index) => (
                <React.Fragment key={stateData.state}>
                  <tr
                    className={`${
                      index % 2 === 0 ? "bg-white" : "bg-gray-50"
                    } hover:bg-gray-50 transition-colors border-b border-gray-200`}
                  >
                    <td className="px-6 py-4">
                      <div className="flex items-center space-x-3">
                        <div>
                          <div className="font-semibold text-gray-900">
                            {applicationStateLabels[
                              stateData.state as keyof typeof applicationStateLabels
                            ] || stateData.state}
                          </div>
                          <div className="text-xs text-gray-500">{stateData.state}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-center">
                      <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-semibold bg-gray-100 text-gray-800">
                        {stateData.total.toLocaleString("tr-TR")}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-center">
                      <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-semibold bg-gray-100 text-gray-800">
                        🏢 {stateData.brands.length}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-center">
                      {stateData.brands.length > 0 && (
                        <button
                          onClick={() => handleStateClick(stateData)}
                          disabled={loadingBrands}
                          className="inline-flex items-center px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-white text-sm font-medium rounded-lg transition-colors"
                        >
                          {loadingBrands ? (
                            <>
                              <div className="inline-block animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white mr-2"></div>
                              <span>Yükleniyor...</span>
                            </>
                          ) : (
                            <>
                              <span className="mr-2">Markalar</span>
                              <span>→</span>
                            </>
                          )}
                        </button>
                      )}
                    </td>
                  </tr>
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Stats Tables - Firma Tarafı */}
      <div className="bg-white rounded-lg shadow-lg overflow-hidden border border-gray-200">
        <div className="bg-gradient-to-r from-gray-300 to-gray-300 px-6 py-4">
          <h2 className="text-xl font-bold text-white flex items-center">
            <span className="mr-2">🏢</span>
            Firma Tarafı
            <span className="ml-4 bg-white/20 px-3 py-1 rounded-full text-sm">
              {companySideTotal.toLocaleString("tr-TR")} Başvuru
            </span>
          </h2>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-100">
              <tr>
                <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">Durum</th>
                <th className="px-6 py-4 text-center text-sm font-semibold text-gray-700">
                  Toplam Başvuru
                </th>
                <th className="px-6 py-4 text-center text-sm font-semibold text-gray-700">
                  Marka Sayısı
                </th>
                <th className="px-6 py-4 text-center text-sm font-semibold text-gray-700">Detay</th>
              </tr>
            </thead>
            <tbody>
              {companySideData.map((stateData, index) => (
                <React.Fragment key={stateData.state}>
                  <tr
                    className={`${
                      index % 2 === 0 ? "bg-white" : "bg-gray-50"
                    } hover:bg-gray-50 transition-colors border-b border-gray-200`}
                  >
                    <td className="px-6 py-4">
                      <div className="flex items-center space-x-3">
                        <div>
                          <div className="font-semibold text-gray-900">
                            {applicationStateLabels[
                              stateData.state as keyof typeof applicationStateLabels
                            ] || stateData.state}
                          </div>
                          <div className="text-xs text-gray-500">{stateData.state}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-center">
                      <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-semibold bg-gray-100 text-gray-800">
                        {stateData.total.toLocaleString("tr-TR")}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-center">
                      <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-semibold bg-gray-100 text-gray-800">
                        🏢 {stateData.brands.length}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-center">
                      {stateData.brands.length > 0 && (
                        <button
                          onClick={() => handleStateClick(stateData)}
                          disabled={loadingBrands}
                          className="inline-flex items-center px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-white text-sm font-medium rounded-lg transition-colors"
                        >
                          {loadingBrands ? (
                            <>
                              <div className="inline-block animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white mr-2"></div>
                              <span>Yükleniyor...</span>
                            </>
                          ) : (
                            <>
                              <span className="mr-2">Markalar</span>
                              <span>→</span>
                            </>
                          )}
                        </button>
                      )}
                    </td>
                  </tr>
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Empty State */}
      {data.length === 0 && (
        <div className="bg-white rounded-lg shadow-lg p-12 text-center">
          <span className="text-6xl mb-4 block">📭</span>
          <h3 className="text-xl font-semibold text-gray-800 mb-2">Veri Bulunamadı</h3>
          <p className="text-gray-600">Şu anda gösterilecek başvuru istatistiği bulunmuyor.</p>
        </div>
      )}

      {/* Info Card */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-start space-x-3">
          <span className="text-2xl">ℹ️</span>
          <div className="flex-1">
            <h4 className="font-semibold text-blue-900 mb-1">Bilgi</h4>
            <p className="text-sm text-blue-800">
              Bu sayfa, son 3 ay içinde yapılan ve aktif durumdaki başvuruları state bazlı olarak
              gösterir. &quot;Markalar&quot; butonuna tıklayarak her state için marka bazında
              detaylı dağılımı görüntüleyebilirsiniz.
            </p>
          </div>
        </div>
      </div>

      {/* Brand Details Modal */}
      <BrandDetailsModal
        isOpen={showBrandModal}
        selectedTransition={selectedModalData}
        onClose={closeBrandModal}
        getStateDisplayName={(state: string) =>
          applicationStateLabels[state as keyof typeof applicationStateLabels] || state
        }
      />
    </div>
  )
}

export default LiveApplicationStats
